import { concat, getContractAddress, keccak256, pad, type Address } from 'viem'
import { inverseChainData, inverseConfigFor } from '@1delta/data-sdk'
import { Call } from '../../../utils/multicall'
import { buildInverseLensCall, usesInverseLens } from './lens'

/**
 * User-data call build for Inverse FiRM. Positions are keyed by OWNER
 * ADDRESS — one CREATE2 escrow per user per Market — so there is NO
 * discovery phase and the layout is fully deterministic from config:
 *
 * per market (config order):
 *   escrow.balance()                — raw collateral (escrow address is
 *                                     predicted LOCALLY, see below)
 *   Market.debts(account)           — static DOLA principal
 *   Market.getCreditLimit(account)  — pessimistic-oracle borrow ceiling
 *   Market.getWithdrawalLimit(account)
 * then once per chain:
 *   DBR.balanceOf(account)
 *   DBR.deficitOf(account)          — > 0 ⇒ force-replenishable AND
 *                                     withdrawals frozen
 *   DBR.signedBalanceOf(account)
 *   DBR.debts(account)              — TOTAL DOLA debt across every market;
 *                                     the burn base for the runway (a sum
 *                                     over the curated markets would miss
 *                                     any market we do not carry)
 *
 * `predictEscrow` is reproduced off-chain: `Market.createEscrow` deploys
 * an EIP-1167 minimal proxy of `escrowImplementation` via CREATE2 with
 * `salt = uint(uint160(user))`, so the address is fully determined by
 * (market, implementation, user) — one multicall round instead of two.
 * A never-deployed escrow has no code; the `balance()` read fails under
 * `allowFailure` and parses as 0, which is exactly right.
 */
export const INVERSE_CALLS_PER_MARKET = 4
export const INVERSE_TAIL_CALLS = 4

/** EIP-1167 init code for `impl`, split around the 20-byte target. */
const proxyInitCode = (impl: Address) =>
  concat([
    '0x3d602d80600a3d3981f3363d3d373d3d3d363d73',
    impl,
    '0x5af43d82803e903d91602b57fd5bf3',
  ])

/** Off-chain `Market.predictEscrow(user)`. */
export function predictInverseEscrow(
  market: Address,
  escrowImplementation: Address,
  user: Address,
): Address {
  return getContractAddress({
    opcode: 'CREATE2',
    from: market,
    salt: pad(user, { size: 32 }),
    bytecodeHash: keccak256(proxyInitCode(escrowImplementation)),
  })
}

export const buildInverseUserCall = (
  chainId: string,
  lender: string,
  account: string,
): Call[] => {
  const cfg = inverseConfigFor(lender, chainId)
  const markets = inverseChainData(lender, chainId)?.markets ?? []
  if (!cfg || markets.length === 0) return []

  // A chain without a lens deployment keeps the per-market walk — the rollout
  // switch and the emergency revert both.
  if (usesInverseLens(chainId)) {
    const lensCall = buildInverseLensCall(
      chainId,
      markets as any,
      cfg.dbr,
      account,
    )
    if (lensCall.length) return lensCall
  }

  const calls: Call[] = markets.flatMap((m) => [
    {
      address: predictInverseEscrow(
        m.address as Address,
        m.escrowImplementation as Address,
        account as Address,
      ),
      name: 'balance',
      params: [],
    },
    { address: m.address, name: 'debts', params: [account] },
    { address: m.address, name: 'getCreditLimit', params: [account] },
    { address: m.address, name: 'getWithdrawalLimit', params: [account] },
  ])
  calls.push(
    { address: cfg.dbr, name: 'balanceOf', params: [account] },
    { address: cfg.dbr, name: 'deficitOf', params: [account] },
    { address: cfg.dbr, name: 'signedBalanceOf', params: [account] },
    { address: cfg.dbr, name: 'debts', params: [account] },
  )
  return calls
}
